import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useMemo,
} from "react"
import PropTypes from "prop-types"
import { useAppContext } from "components/AppContext"

const ChartContext = createContext()

export const useChart = () => {
  const context = useContext(ChartContext)
  if (!context) {
    throw new Error("useChart must be used within a ChartProvider")
  }
  return context
}

export const ChartProvider = ({ children }) => {
  const [toastStatus, setToastStatus] = useState(0)
  const {
    isTreeLoading,
    tree,
    reloadTree,
    reloadCurrentNode,
    treeExpandedState,
    toggleTreeExpandedState,
    isTreeNodeExpanded,
  } = useAppContext()
  const organizeWithState = useCallback(() => {
    reloadTree?.()
  }, [reloadTree])

  const reload = useCallback(
    (
      { organization, currentNode } = { organization: true, currentNode: true }
    ) => {
      if (organization) {
        reloadTree()
      }
      if (currentNode) {
        reloadCurrentNode()
      }
    },
    [reloadTree, reloadCurrentNode]
  )

  const value = useMemo(
    () => ({
      organization: tree,
      organize: organizeWithState,
      reload,
      isLoading: isTreeLoading,
      toastStatus,
      setToastStatus,
      treeExpandedState,
      toggleTreeExpandedState,
      isTreeNodeExpanded,
    }),
    [
      tree,
      organizeWithState,
      reload,
      isTreeLoading,
      toastStatus,
      treeExpandedState,
      toggleTreeExpandedState,
      isTreeNodeExpanded,
    ]
  )

  return <ChartContext.Provider value={value}>{children}</ChartContext.Provider>
}

ChartProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const markIsCurrentNodeBelow = (tree, targetNodeIds = []) => {
  if (!tree) return false

  const targetIds = new Set(
    (targetNodeIds || []).map((id) => Number(id)).filter(Boolean)
  )

  const markNode = (node) => {
    const isTarget = node.is_current_node || targetIds.has(node.id)
    const childOnPath = (node.children || []).map(markNode).some(Boolean)
    const onPath = isTarget || childOnPath

    node.isCurrentNodeBelow = onPath

    return onPath
  }

  return markNode(tree)
}
